/**
 * Shell Application - Main App Component
 * Project: Quantiix Shell (quantiix-shell)
 * Module: src/App.jsx
 *
 * Main shell application component that provides routing and authentication context
 */

import React from 'react';
import {BrowserRouter} from 'react-router-dom';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {DataLayerProvider, clearHierarchyCache} from '@quantiix/data-layer';
import {AuthProvider} from './contexts/AuthContext';
import {CacheProvider} from './contexts/CacheContext';
import {AppRouter} from './components/AppRouter';
import {OperatorRedirect} from './components/OperatorRedirect';
import {MachineContextSeed} from './components/MachineContextSeed';
import {SSEEnabledContent} from './components/SSEEnabledContent';
import CustomerLogo from './components/CustomerLogo';
import {createLogger} from './utils/logger';
import {getEndpoints} from './config/runtimeEndpoints';
import '@quantiix/design-system/dist/design-system.css';
import './App.css';
import I18nProvider from "@/i18n/I18nProvider.jsx";
import {ToastProvider} from "@/contexts/ToastContext.jsx";
import {ToastContainer} from "@/components/ToastContainer.jsx";
import {VersionWatcher} from "@/components/VersionWatcher.jsx";
import {QueryClientBridge} from "@/contexts/QueryClientContext.jsx";

const logger = createLogger('Shell::App');

// Register shell version globally for debugging
if (typeof window !== 'undefined') {
    window.__QUANTIIX_VERSIONS__ = window.__QUANTIIX_VERSIONS__ || {};
    window.__QUANTIIX_VERSIONS__.shell = __APP_VERSION__;
}

logger.highlight(`Shell v${__APP_VERSION__} initialized`, {
    mode: import.meta.env.MODE,
    // Resolved at login, so 'NOT RESOLVED' is the normal state on a cold load.
    region: getEndpoints()?.region || 'NOT RESOLVED',
    gateway: getEndpoints()?.gateway || 'NOT RESOLVED',
    sse: getEndpoints()?.sse || 'NOT RESOLVED',
    authEndpoint: import.meta.env.VITE_AUTH_ENDPOINT || 'NOT SET',
    profileEndpoint: import.meta.env.VITE_PROFILE_ENDPOINT || 'NOT SET',
    logLevel: import.meta.env.VITE_LOG_LEVEL || 'INFO',
    timestamp: new Date().toISOString()
});

// Create QueryClient for Tanstack Query
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000,
            gcTime: 2 * 24 * 60 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            retry: 1,
        },
    },
});

function App() {
    React.useEffect(() => {
        const handleLogout = () => {
            logger.info('Clearing query cache on logout');
            queryClient.clear();
            // queryClient.clear() empties React Query, but the hierarchy is
            // also persisted to sessionStorage by @quantiix/data-layer. Without
            // wiping that too, useCachedHierarchy will re-hydrate the next
            // user's React Query cache from the previous user's hierarchy.
            clearHierarchyCache(queryClient);
        };

        window.addEventListener('shell:logout', handleLogout);
        return () => window.removeEventListener('shell:logout', handleLogout);
    }, []);

    return (
        <div className="App">
            <BrowserRouter>
                <QueryClientProvider client={queryClient}>
                    <QueryClientBridge>
                        <I18nProvider>
                            <AuthProvider>

                                <CacheProvider>
                                    <DataLayerProvider>
                                        <ToastProvider>
                                            <SSEEnabledContent>
                                                <OperatorRedirect>
                                                    <MachineContextSeed/>
                                                    <AppRouter/>
                                                </OperatorRedirect>
                                                <CustomerLogo/>
                                            </SSEEnabledContent>
                                            <ToastContainer/>
                                            <VersionWatcher/>
                                        </ToastProvider>
                                    </DataLayerProvider>
                                </CacheProvider>
                            </AuthProvider>
                        </I18nProvider>
                    </QueryClientBridge>
                    {import.meta.env.DEV && (
                        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
                    )}
                </QueryClientProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;